import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { DeviceDetectorService } from 'ngx-device-detector';
import { forkJoin, map, Observable, of, retry, tap } from 'rxjs';
import { environment } from 'src/environments/environment';

import { DocumentType } from '../_enums/document-type.enum';
import { EntityType } from '../_enums/entity-type.enum';
import { UserEntityTypeEnum } from '../_enums/user-entity-type.enum';
import { sanitizeReturnUrl } from '../_helpers/return-url';
import {
  APIResponse,
  BasicResponse,
  DocumentUploadResponse,
  PresignedUrlResponse,
} from '../_types/api-responses.types';
import {
  Collaborator,
  CustomersPageRequest,
  ExtendedFamilyMember,
  FamilyMember,
  FamilyMemberCreationPayload,
  FamilyMemberCreationRequest,
  FamilyMemberUpdateRequest,
  GetUserAdditionalInfoResponse,
  GetUserCommentsRequest,
  SaveCollaboratorNoteRequest,
  SaveUserCommentRequest,
  User,
  UserDetailsRequest,
  UserSearchResult,
} from '../_types/auth.types';
import { CollaboratorNote } from '../_types/collaborator-note.type';
import {
  GenerateSignatureRequestRequest,
  GenerateSignatureRequestResponse,
  RequestDocumentSignatureRequest,
  UpdateDocumentRequest,
} from '../_types/document.types';
import {
  AgentCommissionDetails,
  GetAgentCommissionsPageRequest,
  GetAgentCommissionsRequest,
  UpdateAgentCommissionRequest,
} from '../_types/fee.types';
import { AlternateLead } from '../_types/lead.types';
import { Notification } from '../_types/notifications.types';
import {
  AllCategoryProductResponse,
  APICategory,
} from '../_types/product.types';
import {
  ProposalDetails,
  ProposalFetchRequest,
} from '../_types/proposals.type';
import { UserComment } from '../_types/user-comment.type';

@Injectable({
  providedIn: 'root',
})
export class UserAuthService implements OnDestroy {
  logoutTimeoutId!: ReturnType<typeof setTimeout>;

  private readonly boundStorageHandler: (event: StorageEvent) => void;

  _baseURL: any;

  deviceInfo: any;

  _headers: any;

  _headersOTP: any;

  _user: any;

  _headersWithToken: any;

  _CommunicatorbaseURL: any;

  _CataloguebaseURL: any;

  _headersCatalogueToken: any;

  _FeebaseURL: string;

  _DocumentbaseURL: any;

  _headersWithFile: any;

  _InsurancebaseURL: any;

  constructor(
    private httpClient: HttpClient,
    private deviceService: DeviceDetectorService,
    private router: Router
  ) {
    this._baseURL = environment.USERAPIENDPOINT;
    this._CommunicatorbaseURL = environment.COMMUNICATORAPIURL;
    this._CataloguebaseURL = environment.CATALOGUEAPIURL;
    this._FeebaseURL = environment.FEEAPIENDPOINT;
    this._DocumentbaseURL = environment.DOCUMENTAPIURL;
    this._InsurancebaseURL = environment.INSURANCEAPIURL;
    this.deviceInfo = this.deviceService.getDeviceInfo();
    this.boundStorageHandler = this.handleStorageChange.bind(this);
    window.addEventListener('storage', this.boundStorageHandler);

    this.regenerateHeaders();
  }

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.boundStorageHandler);
  }

  private handleStorageChange(event: StorageEvent): void {
    if (event.key !== 'user') {
      return;
    }

    if (event.newValue) {
      this.getCurrentUser();
      this.regenerateHeaders();
      this.handleTokenInit();
      return;
    }

    if (this.logoutTimeoutId) {
      clearTimeout(this.logoutTimeoutId);
    }
    // the session died in another tab, this tab did not ask for it: bring the user back here
    this.navigateToLogin(true);
  }

  regenerateHeaders() {
    const devicedetail: any = {
      platform: 'Website',
      browser: this.deviceInfo.browser,
      browserVersion: this.deviceInfo.browser_version,
      osVersion: this.deviceInfo.os_version,
      deviceId: this.deviceInfo.device + this.deviceInfo.deviceType,
      appVersion: '',
      ipAddress: '',
      macAddress: '',
    };
    this._headers = new HttpHeaders()
      .set('content-type', 'application/json')
      .set('User_Agent', JSON.stringify(devicedetail));
    this._headersOTP = new HttpHeaders()
      .set('content-type', 'application/json')
      .set('data-hash', 'test')
      .set('device-id', 'test')
      .set('User_Agent', JSON.stringify(devicedetail));
    if (this.isUserLoggedIn()) {
      this.getCurrentUser();

      this._headersWithToken = new HttpHeaders()
        .set('content-type', 'application/json')
        .set('Access-Token', this._user.token)
        .set('User_Agent', JSON.stringify(devicedetail));
      this._headersCatalogueToken = new HttpHeaders()
        .set('content-type', 'application/json')
        .set('Access-Token', this._user.token)
        .set('User_Agent', JSON.stringify(devicedetail));
      this._headersWithFile = new HttpHeaders()
        .set('User_Agent', JSON.stringify(devicedetail))
        .set('Access-Token', this._user.token);
    }
  }

  isUserLoggedIn(): boolean {
    const data = localStorage.getItem('user');
    if (data != null) {
      return true;
    }
    return false;
  }

  getCurrentUser(): any {
    const data: any = localStorage.getItem('user');
    if (data != null) {
      this._user = JSON.parse(data);
    }
    return this._user;
  }

  login(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}admin/signin`, obj, {
      headers: this._headers,
    });
  }

  searchUsersLike(
    query: string
  ): Observable<APIResponse<{ customers: UserSearchResult[] }>> {
    return this.httpClient.get<APIResponse<{ customers: UserSearchResult[] }>>(
      `${this._baseURL}user/search/like`,
      { headers: this._headersWithToken, params: { query } }
    );
  }

  customers(obj: any): Observable<APIResponse<{ usersList: User[] }>> {
    return this.httpClient.post<APIResponse<{ usersList: User[] }>>(
      `${this._baseURL}admin/get/users`,
      obj,
      {
        headers: this._headersWithToken,
      }
    );
  }

  customersPage(
    obj: CustomersPageRequest
  ): Observable<APIResponse<{ membersList: ExtendedFamilyMember[] }>> {
    return this.httpClient.post<
      APIResponse<{ membersList: ExtendedFamilyMember[] }>
    >(`${this._baseURL}admin/get/customers`, obj, {
      headers: this._headersWithToken,
    });
  }

  exportCustomers(obj: CustomersPageRequest): Observable<Blob> {
    return this.httpClient.post(`${this._baseURL}user/export/customers`, obj, {
      headers: this._headersWithToken,
      responseType: 'blob',
    });
  }

  customersByPartner(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}user/get/partner/users`, obj, {
      headers: this._headersWithToken,
    });
  }

  familymembers(
    obj: any
  ): Observable<APIResponse<{ familyMemberList: FamilyMember[] }>> {
    return this.httpClient.post<
      APIResponse<{ familyMemberList: FamilyMember[] }>
    >(`${this._baseURL}user/get/user/family-member`, obj, {
      headers: this._headersWithToken,
    });
  }

  familymembersDetail(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}user/get/family-member`, obj, {
      headers: this._headersWithToken,
    });
  }

  partners(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}partner/get/active/partners`,
      {
        searchBy: obj?.searchBy ? obj.searchBy : '',
      },
      { headers: this._headersWithToken }
    );
  }

  Allpartners(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}admin/get/partners`, obj, {
      headers: this._headersWithToken,
    });
  }

  Allagents(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}admin/get/agents`, obj, {
      headers: this._headersWithToken,
    });
  }

  assignpartner(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}admin/assign/partner`, obj, {
      headers: this._headersWithToken,
    });
  }

  assignAgent(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}partner/assign/agent`, obj, {
      headers: this._headersWithToken,
    });
  }

  UpdatepartnerState(obj: any): Observable<any> {
    return this.httpClient.put(
      `${this._baseURL}admin/update/partner/state`,
      obj,
      { headers: this._headersWithToken }
    );
  }

  UpdateuserState(obj: any): Observable<any> {
    return this.httpClient.put(`${this._baseURL}admin/update/user/state`, obj, {
      headers: this._headersWithToken,
    });
  }

  approvepartner(obj: any): Observable<any> {
    return this.httpClient.put(`${this._baseURL}partner/block`, obj, {
      headers: this._headersWithToken,
    });
  }

  getAdminByID(): Observable<any> {
    return this.httpClient.get(`${this._baseURL}admin/get/profiles`, {
      headers: this._headersWithToken,
    });
  }

  getAdminProfile(): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}admin/get/profiles`,
      { adminId: this._user.adminId },
      { headers: this._headersWithToken }
    );
  }

  getPartnerDetail(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}partner/details`, obj, {
      headers: this._headersWithToken,
    });
  }

  getCustomerDetail(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}user/details`, obj, {
      headers: this._headersWithToken,
    });
  }

  UpdateCustomer(obj: any): Observable<any> {
    return this.httpClient.put(`${this._baseURL}user/update`, obj, {
      headers: this._headersWithToken,
    });
  }

  UpdateProfile(obj: any): Observable<any> {
    return this.httpClient.put(`${this._baseURL}admin/update`, obj, {
      headers: this._headersWithToken,
    });
  }

  UpdatePassword(obj: any): Observable<any> {
    return this.httpClient.put(
      `${this._baseURL}admin/change/password`,
      {
        adminId: this._user.adminId,
        oldPassword: obj.oldPassword,
        newPassword: obj.newPassword,
      },
      { headers: this._headersWithToken }
    );
  }

  forgetpassword(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}admin/forgot/password`, obj, {
      headers: this._headers,
    });
  }

  setpassword(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}admin/verify/forgot/password`,
      obj,
      { headers: this._headers }
    );
  }

  resendotp(obj: any): Observable<any> {
    return this.httpClient.put(`${this._CommunicatorbaseURL}otp/resend`, obj, {
      headers: this._headersOTP,
    });
  }

  GetNotificationList(): Observable<
    APIResponse<{ notificationList: Notification[] }>
  > {
    return this.httpClient.get<
      APIResponse<{ notificationList: Notification[] }>
    >(`${this._CommunicatorbaseURL}communication/get/notification`, {
      headers: this._headersWithToken,
    });
  }

  readNotifications(): Observable<void> {
    return this.httpClient.put<void>(
      `${this._CommunicatorbaseURL}communication/read/notification`,
      {},
      { headers: this._headersWithToken }
    );
  }

  _addCategory(obj: any): Observable<any> {
    return this.httpClient.post(`${this._CataloguebaseURL}category/add`, obj, {
      headers: this._headersCatalogueToken,
    });
  }

  _updateSubcategory(obj: any): Observable<any> {
    return this.httpClient.put(
      `${this._CataloguebaseURL}category/update/subcategory`,
      obj,
      { headers: this._headersCatalogueToken }
    );
  }

  _listAllCategory(request: {
    withoutProducts?: boolean;
    searchBy?: string;
    partnerId?: string;
    insuranceTypes?: string[];
  }): Observable<APIResponse<AllCategoryProductResponse>> {
    return this.httpClient.post<APIResponse<AllCategoryProductResponse>>(
      `${this._CataloguebaseURL}category/get/all`,
      request,
      { headers: this._headersCatalogueToken }
    );
  }

  _getAllSubcategory(
    obj: any
  ): Observable<APIResponse<{ subCategoryList: APICategory[] }>> {
    return this.httpClient.post<
      APIResponse<{ subCategoryList: APICategory[] }>
    >(`${this._CataloguebaseURL}category/get/subcategories`, obj, {
      headers: this._headersCatalogueToken,
    });
  }

  _getCategoryById(obj: any): Observable<any> {
    return this.httpClient.post(`${this._CataloguebaseURL}category/get`, obj, {
      headers: this._headersCatalogueToken,
    });
  }

  _listAllProduct(obj: {
    categoryId: string;
    searchBy: string;
    rootProductsOnly?: boolean;
  }): Observable<any> {
    return this.httpClient.post(
      `${this._CataloguebaseURL}product/get/category`,
      obj,
      { headers: this._headersCatalogueToken }
    );
  }

  addPartner(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}partner/signup`, obj, {
      headers: this._headersWithToken,
    });
  }

  updatePartner(obj: any): Observable<any> {
    return this.httpClient.put(`${this._baseURL}partner/update`, obj, {
      headers: this._headersWithToken,
    });
  }

  _getCommissionDetailsPageByAgent(
    obj: GetAgentCommissionsPageRequest
  ): Observable<APIResponse<{ feeDetailsList: AgentCommissionDetails[] }>> {
    return this.httpClient.post<
      APIResponse<{ feeDetailsList: AgentCommissionDetails[] }>
    >(`${this._FeebaseURL}get/agent/agent-commission/page`, obj, {
      headers: this._headersWithToken,
    });
  }

  _deleteCommision(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._FeebaseURL}delete/agent-commission`,
      obj,
      { headers: this._headersWithToken }
    );
  }

  _getFeeCategory(obj: any): Observable<any> {
    return this.httpClient.post(`${this._FeebaseURL}get/category`, obj, {
      headers: this._headersWithToken,
    });
  }

  _getFeeSubCategory(obj: any): Observable<any> {
    return this.httpClient.post(`${this._FeebaseURL}get/sub-category`, obj, {
      headers: this._headersWithToken,
    });
  }

  _updateCommission(
    obj: UpdateAgentCommissionRequest
  ): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.put<APIResponse<BasicResponse>>(
      `${this._FeebaseURL}update/agent-commission`,
      obj,
      { headers: this._headersWithToken }
    );
  }

  _addCommission(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._FeebaseURL}add/agent-commission`,
      obj,
      { headers: this._headersWithToken }
    );
  }

  UploadDocument(obj: any): Observable<APIResponse<DocumentUploadResponse>> {
    const form = new FormData();
    form.append('docName', obj.docName);
    form.append('userId', obj.userId);
    if (obj.memberId !== undefined) {
      form.append('memberId', obj.memberId);
    }
    if (obj.uploadedBy !== undefined) {
      form.append('uploadedBy', obj.uploadedBy);
    } else {
      form.append('uploadedBy', this._user.adminId);
    }
    if (obj.doc !== undefined) {
      form.append('doc', obj.doc, obj.doc.name);
    }
    if (obj.type) {
      form.append('type', obj.type);
    }
    if (obj.isPublic) {
      form.append('public', obj.isPublic);
    }
    // document-store publishes by default, so only an explicit false matters.
    if (obj.isPublished !== undefined) {
      form.append('published', obj.isPublished);
    }

    return this.httpClient.post<APIResponse<DocumentUploadResponse>>(
      `${this._DocumentbaseURL}upload`,
      form,
      {
        headers: this._headersWithFile,
      }
    );
  }

  updateDocument(
    obj: UpdateDocumentRequest
  ): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.put<APIResponse<BasicResponse>>(
      `${this._DocumentbaseURL}update`,
      {
        ...obj,
        entityType: UserEntityTypeEnum.ADMIN_ID,
        entityId: this._user.adminId,
      },
      { headers: this._headersWithToken }
    );
  }

  getUserLeadList(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._InsurancebaseURL}get/user/leads`,
      obj,
      { headers: this._headersWithToken }
    );
  }

  Allleads(obj: any): Observable<any> {
    return this.httpClient.post(`${this._InsurancebaseURL}get/leads`, obj, {
      headers: this._headersWithToken,
    });
  }

  getadminDashboardInfo(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._InsurancebaseURL}get/admin/dashboard-info`,
      obj,
      { headers: this._headersWithToken }
    );
  }

  AllProposals(
    obj: ProposalFetchRequest,
    fetchDocuments: boolean = false
  ): Observable<any> {
    return this.httpClient.post(
      `${this._InsurancebaseURL}get/proposals`,
      { ...obj, fetchDocuments },
      {
        headers: this._headersWithToken,
      }
    );
  }

  _AllIsuranceType(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._InsurancebaseURL}get/static/data`,
      obj,
      { headers: this._headersWithToken }
    );
  }

  _AddExistingContract(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._InsurancebaseURL}add/existing/contract`,
      obj,
      { headers: this._headersWithToken }
    );
  }

  getExpiringContracts(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._InsurancebaseURL}get/expired/proposals`,
      obj,
      { headers: this._headersWithToken }
    );
  }

  getAgendaList(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}agenda/get/agenda`, obj, {
      headers: this._headersWithToken,
    });
  }

  GetProposalDetail(obj: any): Observable<APIResponse<ProposalDetails>> {
    // obj['userId'] = this._user.userId;
    return this.httpClient.post<APIResponse<ProposalDetails>>(
      `${this._InsurancebaseURL}get/proposal`,
      obj,
      {
        headers: this._headersWithToken,
      }
    );
  }

  GetLeadDetail(obj: any): Observable<any> {
    // obj['userId'] = this._user.userId;
    return this.httpClient.post(
      `${this._InsurancebaseURL}get/lead/details`,
      { ...obj, entityType: EntityType.ADMIN_ID, entityId: this._user.adminId },
      { headers: this._headersWithToken }
    );
  }

  AllSubmissions(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._InsurancebaseURL}get/submissions`,
      obj,
      { headers: this._headersWithToken }
    );
  }

  AllDocuments(obj: any): Observable<any> {
    return this.httpClient.post(`${this._DocumentbaseURL}get/user/docs`, obj, {
      headers: this._headersWithToken,
    });
  }

  AllMemberDocuments(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._DocumentbaseURL}get/member/docs`,
      obj,
      { headers: this._headersWithToken }
    );
  }

  registration(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}user/signup`, obj, {
      headers: this._headersWithToken,
    });
  }

  _getActiveCustomerList(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}get/partner/active/users`,
      obj,
      { headers: this._headersWithToken }
    );
  }

  _getActiveAllCustomerList(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}user/get/all/active`, obj, {
      headers: this._headersWithToken,
    });
  }

  _getAgendaList(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}agenda/get/agenda`, obj, {
      headers: this._headersWithToken,
    });
  }

  _addAgenda(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}agenda/add/agenda`, obj, {
      headers: this._headersWithToken,
    });
  }

  _updateAgenda(obj: any): Observable<any> {
    return this.httpClient.put(`${this._baseURL}agenda/update/agenda`, obj, {
      headers: this._headersWithToken,
    });
  }

  _deleteAgenda(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}agenda/delete/agenda`, obj, {
      headers: this._headersWithToken,
    });
  }

  _synAgendaGoogleCalendar(obj: any, gtoken: any): Observable<any> {
    // obj={
    //   'summary': 'Google I/O 2015',
    //   'location': '800 Howard St., San Francisco, CA 94103',
    //   'description': 'A chance to hear more about Google\'s developer products.',
    //   'start': {
    //     'dateTime': '2023-08-28T09:00:00-07:00',
    //     'timeZone': 'America/Los_Angeles',
    //   },
    //   'end': {
    //     'dateTime': '2023-08-30T17:00:00-07:00',
    //     'timeZone': 'America/Los_Angeles',
    //   }
    // }

    return this.httpClient.post(
      'https://www.googleapis.com/calendar/v3/calendars/primary/events',
      obj,
      { headers: { Authorization: `Bearer ${gtoken}` } }
    );
  }

  _getModuleList(): Observable<any> {
    return this.httpClient.get(`${this._baseURL}admin/get/modules`, {
      headers: this._headersWithToken,
    });
  }

  _getCsaList(): Observable<any> {
    return this.httpClient.get(`${this._baseURL}admin/get/csa`, {
      headers: this._headersWithToken,
    });
  }

  addCsa(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}admin/add/csa`, obj, {
      headers: this._headersWithToken,
    });
  }

  updateCsa(obj: any): Observable<any> {
    return this.httpClient.put(`${this._baseURL}admin/update/csa`, obj, {
      headers: this._headersWithToken,
    });
  }

  deleteCsa(adminId: string): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}admin/delete/csa`,
      { adminId },
      {
        headers: this._headersWithToken,
      }
    );
  }

  getCsaDetail(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}admin/get/csa/details`, obj, {
      headers: this._headersWithToken,
    });
  }

  UpdateCsaState(obj: any): Observable<any> {
    return this.httpClient.put(`${this._baseURL}admin/update/csa/state`, obj, {
      headers: this._headersWithToken,
    });
  }

  _logout(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}partner/signout`, obj, {
      headers: this._headers,
    });
  }

  getPresignedUrl(key: string): Observable<APIResponse<PresignedUrlResponse>> {
    return this.httpClient.post<APIResponse<PresignedUrlResponse>>(
      `${this._DocumentbaseURL}get/presigned`,
      { key },
      {
        headers: this._headersWithToken,
      }
    );
  }

  getUserDetails(userId: string): Observable<APIResponse<User>> {
    const obj: UserDetailsRequest = {
      entityId: userId,
      entityType: EntityType.USER_ID,
    };
    return this.httpClient.post<APIResponse<User>>(
      `${this._baseURL}user/details`,
      obj,
      {
        headers: this._headersWithToken,
      }
    );
  }

  checkTokenServerSide() {
    if (this.isUserLoggedIn()) {
      this.getAdminByID().subscribe({
        next: () => {},
        error: () => {
          localStorage.removeItem('user');
          window.location.reload();
        },
      });
    }
  }

  getUserComments(
    obj: GetUserCommentsRequest
  ): Observable<APIResponse<{ comments: UserComment[] }>> {
    return this.httpClient.post<APIResponse<{ comments: UserComment[] }>>(
      `${this._baseURL}admin/comments/get`,
      obj,
      {
        headers: this._headersWithToken,
      }
    );
  }

  getMemberComments(
    obj: GetUserCommentsRequest
  ): Observable<APIResponse<{ comments: UserComment[] }>> {
    return this.httpClient.post<APIResponse<{ comments: UserComment[] }>>(
      `${this._baseURL}admin/comments/get/member`,
      obj,
      {
        headers: this._headersWithToken,
      }
    );
  }

  saveUserComment(
    obj: SaveUserCommentRequest
  ): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this._baseURL}admin/comments/save`,
      obj,
      {
        headers: this._headersWithToken,
      }
    );
  }

  verifyOtp(obj: any): Observable<any> {
    return this.httpClient.post(`${this._baseURL}admin/perform/action`, obj, {
      headers: this._headers,
    });
  }

  _resendOtp(obj: any): Observable<any> {
    const device: any = `${Date.now()}lty`;
    const header1 = new HttpHeaders()
      .set('accept', 'application/json')
      .set('data-hash', 'testww1')
      .set('device-id', device)
      .set('Content-Type', 'application/json');
    return this.httpClient.put(`${this._CommunicatorbaseURL}otp/resend`, obj, {
      headers: header1,
    });
  }

  hardDeleteUser(userId: string): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this._baseURL}admin/hard-delete/user`,
      { userId },
      { headers: this._headersWithToken }
    );
  }

  hardDeleteMember(
    familyMemberId: string
  ): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this._baseURL}admin/hard-delete/member`,
      { familyMemberId },
      { headers: this._headersWithToken }
    );
  }

  getLeadsAwaitingAdmin(): Observable<
    APIResponse<{ leadsList: AlternateLead[] }>
  > {
    return this.httpClient.post<APIResponse<{ leadsList: AlternateLead[] }>>(
      `${this._InsurancebaseURL}get/leads/awaiting-admin`,
      {
        entityType: 'ADMIN_ID',
        entityId: 'admin123',
      },
      { headers: this._headersWithToken }
    );
  }

  getLeadsAwaitingUser(): Observable<
    APIResponse<{ leadsList: AlternateLead[] }>
  > {
    return this.httpClient.post<APIResponse<{ leadsList: AlternateLead[] }>>(
      `${this._InsurancebaseURL}get/leads/awaiting-user`,
      {
        entityType: 'ADMIN_ID',
        entityId: 'admin123',
      },
      { headers: this._headersWithToken }
    );
  }

  addFamilyMember(
    obj: FamilyMemberCreationRequest
  ): Observable<APIResponse<FamilyMemberCreationPayload>> {
    return this.httpClient.post<APIResponse<FamilyMemberCreationPayload>>(
      `${this._baseURL}user/add/family-member`,
      obj,
      { headers: this._headersWithToken }
    );
  }

  updateFamilyMember(
    obj: FamilyMemberUpdateRequest
  ): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.put<APIResponse<BasicResponse>>(
      `${this._baseURL}user/update/family-member`,
      obj,
      { headers: this._headersWithToken }
    );
  }

  /**
   * Uploads the files and keeps the whole document-store answer for each.
   *
   * Callers that link the uploads to something else need the `documentId`:
   * a proposal's policy and offer papers are recorded by id, not by key.
   */
  uploadDocuments(
    files: File[],
    userId: string,
    memberId: string,
    type?: DocumentType
  ): Observable<DocumentUploadResponse[]> {
    if (files.length === 0) return of([]);

    return forkJoin(
      files.map((file) =>
        this.UploadDocument({
          userId,
          memberId,
          uploadedBy: this._user.adminId,
          doc: file,
          docName: file.name,
          type,
        }).pipe(
          map((res: APIResponse<DocumentUploadResponse>) => {
            if (res.error) throw new Error(res.error.clientErrorMessage);
            return res.data;
          }),
          retry(3)
        )
      )
    );
  }

  uploadFiles(
    files: File[],
    userId: string,
    memberId: string,
    type?: DocumentType
  ): Observable<string[]> {
    return this.uploadDocuments(files, userId, memberId, type).pipe(
      map((documents) => documents.map((document) => document.key))
    );
  }

  deleteDocument(documentId: number, hardDelete: boolean = false) {
    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this._DocumentbaseURL}delete`,
      {
        id: documentId,
        hardDelete,
        entityType: UserEntityTypeEnum.ADMIN_ID,
        entityId: this._user.adminId,
      },
      { headers: this._headersWithToken }
    );
  }

  generateUserPassword(userId: string) {
    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this._baseURL}admin/user/generate-password`,
      { userId },
      { headers: this._headersWithToken }
    );
  }

  getNonUserMembers() {
    return this.httpClient.post<
      APIResponse<{ membersList: ExtendedFamilyMember[] }>
    >(
      `${this._baseURL}admin/get/non-user-members`,
      {},
      { headers: this._headersWithToken }
    );
  }

  requestDocumentSignature(
    obj: RequestDocumentSignatureRequest
  ): Observable<APIResponse<DocumentUploadResponse>> {
    const form = new FormData();
    form.append('userId', obj.userId);
    form.append('memberId', obj.memberId);
    obj.files.forEach((x) => {
      form.append('files', x, x.name);
    });

    return this.httpClient.post<APIResponse<DocumentUploadResponse>>(
      `${this._DocumentbaseURL}request-signature`,
      form,
      {
        headers: this._headersWithFile,
      }
    );
  }

  handleTokenInit() {
    if (this.isUserLoggedIn() && this.getCurrentUser().expirationTime) {
      const expirationTime = this._user.expirationTime!;
      if (!Number.isNaN(expirationTime) && Number(expirationTime) > 0) {
        if (Number(expirationTime) < new Date().getTime()) {
          this.logout(true);
        } else {
          if (this.logoutTimeoutId) {
            clearTimeout(this.logoutTimeoutId);
          }
          this.logoutTimeoutId = setTimeout(
            () => {
              this.logout(true);
            },
            Number(expirationTime) - new Date().getTime()
          );
        }
      }
    }
  }

  /**
   * @param preserveReturnUrl keep the current page as the post-login destination. Used when the
   * session dies on its own (expiry, failed refresh), not when the user signs out deliberately.
   */
  logout(preserveReturnUrl = false) {
    localStorage.removeItem('loginTime');
    localStorage.removeItem('user');
    if (this.logoutTimeoutId) {
      clearTimeout(this.logoutTimeoutId);
    }
    this.navigateToLogin(preserveReturnUrl);
  }

  private navigateToLogin(preserveReturnUrl: boolean): void {
    const returnUrl = preserveReturnUrl
      ? sanitizeReturnUrl(this.router.url)
      : null;
    this.router.navigate(
      ['/login'],
      returnUrl ? { queryParams: { returnUrl } } : {}
    );
  }

  deletePartner(partnerId: string) {
    return this.httpClient.post<
      APIResponse<{ membersList: ExtendedFamilyMember[] }>
    >(
      `${this._baseURL}partner/delete`,
      { partnerId },
      { headers: this._headersWithToken }
    );
  }

  getUserAdditionalInfo(userId: string, isMember: boolean = false) {
    return this.httpClient.post<APIResponse<GetUserAdditionalInfoResponse>>(
      `${this._baseURL}user/get/additional-info${isMember ? '/member' : ''}`,
      { userId },
      { headers: this._headersWithToken }
    );
  }

  updateUserAdditionalInfo(
    userId: string,
    additionalInfo: string,
    isMember: boolean = false
  ) {
    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this._baseURL}user/set/additional-info${isMember ? '/member' : ''}`,
      { userId, additionalInfo },
      { headers: this._headersWithToken }
    );
  }

  sendDocumentSignatureRequest(
    obj: GenerateSignatureRequestRequest
  ): Observable<APIResponse<GenerateSignatureRequestResponse>> {
    return this.httpClient.post<APIResponse<GenerateSignatureRequestResponse>>(
      `${this._DocumentbaseURL}contract/yousign/generate-request`,
      obj,
      { headers: this._headersWithToken }
    );
  }

  // Collaborator CRUD methods
  addCollaborator(
    obj: any
  ): Observable<APIResponse<{ collaboratorId: string }>> {
    return this.httpClient.post<APIResponse<{ collaboratorId: string }>>(
      `${this._baseURL}user/add/collaborator`,
      obj,
      { headers: this._headersWithToken }
    );
  }

  getCollaborator(id: string): Observable<APIResponse<Collaborator>> {
    return this.httpClient.post<APIResponse<Collaborator>>(
      `${this._baseURL}user/get/collaborator`,
      { id },
      { headers: this._headersWithToken }
    );
  }

  getCollaboratorsByUser(
    userId: string
  ): Observable<APIResponse<{ collaborators: Collaborator[] }>> {
    return this.httpClient.post<APIResponse<{ collaborators: Collaborator[] }>>(
      `${this._baseURL}user/get/collaborators/user`,
      { userId },
      { headers: this._headersWithToken }
    );
  }

  updateCollaborator(obj: any): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.put<APIResponse<BasicResponse>>(
      `${this._baseURL}user/update/collaborator`,
      obj,
      { headers: this._headersWithToken }
    );
  }

  deleteCollaborator(id: string): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this._baseURL}user/delete/collaborator`,
      { id },
      { headers: this._headersWithToken }
    );
  }

  getCollaboratorNotes(
    collaboratorId: string
  ): Observable<APIResponse<{ notes: CollaboratorNote[] }>> {
    return this.httpClient.post<APIResponse<{ notes: CollaboratorNote[] }>>(
      `${this._baseURL}admin/notes/get/collaborator`,
      { collaboratorId },
      { headers: this._headersWithToken }
    );
  }

  saveCollaboratorNote(
    obj: SaveCollaboratorNoteRequest
  ): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this._baseURL}admin/notes/save`,
      obj,
      {
        headers: this._headersWithToken,
      }
    );
  }

  refreshToken(): Observable<any> {
    if (!this.isUserLoggedIn()) {
      return new Observable((observer) => {
        observer.error('User not logged in');
        observer.complete();
      });
    }

    const currentUser = this.getCurrentUser();
    return this.httpClient
      .post(
        `${this._baseURL}token/regenerate/token`,
        {
          token: currentUser.token,
        },
        {
          headers: this._headers,
        }
      )
      .pipe(
        tap((result: any) => {
          if (result.status === 'SUCCESS' && result.data) {
            // Update the user data in localStorage with new token and expiration time
            const updatedUser = {
              ...currentUser,
              token: result.data.token,
              expirationTime: result.data.expiresAt,
            };
            localStorage.setItem('user', JSON.stringify(updatedUser));

            // Update the current user object
            this._user = updatedUser;

            // Regenerate headers with new token
            this.regenerateHeaders();

            // Reinitialize token handling with new expiration time
            this.handleTokenInit();
          }
        })
      );
  }

  // We use this function to compare the version in the API to the version in front
  // If it's different, it means that this version of the front is cached so we reload it
  checkVersion() {
    const headers = new HttpHeaders().set(
      'Content-Type',
      'text/plain; charset=utf-8'
    );
    this.httpClient
      .get(`${environment.BASE_URL}version`, { headers, responseType: 'text' })
      .subscribe({
        next: (x) => {
          if (environment.VERSION !== x && !localStorage.getItem('reloaded')) {
            localStorage.setItem('reloaded', JSON.stringify(true));
            // Reload without cache
            const { href } = window.location;
            window.location.href = href;
          } else {
            localStorage.removeItem('reloaded');
          }
        },
      });
  }
}
